/**
 * Bulk-transfer dialog for the site grid's selection bar — picks the
 * destination group for the selected sites' primary memberships.
 *
 * Staff choose the destination customer first (preseeded from the grid's
 * scope customer) and then a group under it; everyone else transfers
 * within their own customer, so only the group picker shows, and the
 * backend scopes its options to groups the user can access.
 */
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@sophocles/app';
import CustomerAutocomplete from '@sophocles/components/CustomerAutocomplete';
import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';

export default function TransferSitesDialog({ open, onClose, onConfirm, customerId, currentSiteGroupId, count }) {
  const { t } = useTranslation();
  const { user, isStaff } = useUser();
  const siteTerm = useSiteTerm();
  const siteGroupTerm = useSiteGroupTerm();
  const ownCustomerId = user.get('customer', null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedSiteGroupId, setSelectedSiteGroupId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSelectedCustomerId(customerId ?? null);
    setSelectedSiteGroupId(null);
  }, [open, customerId]);

  // Non-staff are pinned to the grid's scope customer (their own when the
  // grid is mounted unscoped); staff pick freely.
  const effectiveCustomerId = isStaff ? selectedCustomerId : (customerId ?? ownCustomerId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('sites.transferCountSites', 'Transfer {{count}} {{site}}', {
          count,
          site: count === 1 ? siteTerm.singular.toLowerCase() : siteTerm.plural.toLowerCase(),
        })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isStaff && (
            <CustomerAutocomplete
              fullWidth
              value={selectedCustomerId}
              onChange={(e, nextCustomerId) => {
                setSelectedCustomerId(nextCustomerId);
                setSelectedSiteGroupId(null);
              }}
            />
          )}
          {!!effectiveCustomerId && (
            <SiteGroupAutocomplete
              excludeInventory
              fullWidth
              customerId={effectiveCustomerId}
              value={selectedSiteGroupId}
              filters={{ is_virtual: false }}
              ignoreIds={currentSiteGroupId ? [currentSiteGroupId] : []}
              label={t('sites.destinationSiteGroup', 'Destination {{siteGroup}}', {
                siteGroup: siteGroupTerm.singular,
              })}
              onChange={(e, siteGroupId) => setSelectedSiteGroupId(siteGroupId)}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel', 'Cancel')}</Button>
        <Button onClick={() => onConfirm(selectedSiteGroupId)} variant="contained" disabled={!selectedSiteGroupId}>
          {t('sites.transfer', 'Transfer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
